import React, { useState, useEffect, Component } from 'react';
import {
    Button,
    Modal,
    Form,
    Input,
    Radio,
    Select,
    Spin,
    message,
    Tooltip
} from 'antd';
import { SettingOutlined, CloseOutlined } from '@ant-design/icons';

import { getJSON } from './RestHandler';
import { dataService } from './dataService';
import { setIsPageLoading } from './GlobalParams';

const { Option } = Select;

interface Values {
    title: string;
    description: string;
    modifier: string;
}

interface CollectionCreateFormProps {
    visible: boolean;
    onCreate: (values: Values) => void;
    onCancel: () => void;
    configValues: {};
    filePath: String;
    fileId: String;
}

let timeout;
let currentValue;
const fetchData = (url, query, callback) => {
    if (timeout) {
        clearTimeout(timeout);
        timeout = null;
    }
    currentValue = query;

    const getData = () => {
        getJSON(`${url}?${query}`).then((d) => {
            if (currentValue === query) {
                const result = d;
                if (d && d.length > 0) {
                    const data = [];
                    result.forEach((r) => {
                        data.push({
                            value: r.value,
                            text: r.label
                        });
                    });
                    callback(data);
                } else {
                    callback([{ value: null, text: 'No Data' }]);
                }
            }
        });
    };
    timeout = setTimeout(getData, 300);
};

const selectProp = {
    filterOption: false,
    defaultActiveFirstOption: false,
    showArrow: false,
    notFoundContent: null,
    showSearch: true,
    showArrow: true
};

const CollectionCreateForm: React.FC<CollectionCreateFormProps> = ({
    visible,
    onCreate,
    onCancel,
    configValues,
    filePath,
    fileId,

    makerData,
    setMakerData,
    modelData,
    setModelData,
    builderData,
    setBuilderData,
    versionData,
    setVersionData,

    makerText,
    setMakerText,
    modelText,
    setModelText,
    builderText,
    setBuilderText,
    versionText,
    setVersionText
}) => {
    const makerHandleSearch = (value) => {
        setMakerData([]);
        fetchData(`/Home/GetMaker`, `query=${value}`, (data) =>
            setMakerData(data)
        );
    };

    const modelHandleSearch = (value) => {
        setModelLoading(true);
        fetchData(
            `/Home/GetModel`,
            `query=${value}&makerCode=${form.getFieldValue('maker')}`,
            (data) => {
                setModelData(data);
                setModelLoading(false);
            }
        );
    };

    const builderHandleSearch = (value) => {
        setBuilderData([]);
        fetchData(`/Home/GetBuilder`, `query=${value}`, (data) =>
            setBuilderData(data)
        );
    };

    const versionHandleSearch = (value) => {
        setVersionLoading(true);
        setVersionData([]);
        fetchData(`/Home/GetVersion`, `query=${value}`, (data) => {
            setVersionData(data);
            setVersionLoading(false);
        });
    };

    const makerOptions = makerData.map((d) => (
        <Option key={d.value}>{d.text}</Option>
    ));
    const modelOptions = modelData.map((d) => (
        <Option key={d.value}>{d.text}</Option>
    ));
    const builderOptions = builderData.map((d) => (
        <Option key={d.value} value={d.value}>
            {d.text}
        </Option>
    ));
    const versionOptions = versionData.map((d) => (
        <Option key={d.value}>{d.text}</Option>
    ));

    const [isDirty, setIsDirty] = useState(false);
    const onValuesChange = (values) => {
        setIsDirty(true);
    };

    const [modelLoading, setModelLoading] = useState(false);
    const [versionLoading, setVersionLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue(configValues);
        setMakerText(configValues['makerText']);
        setModelText(configValues['modelText']);
        setBuilderText(configValues['builderText']);
        setVersionText(configValues['versionText']);
    }, [configValues]);

    return (
        <Modal
            forceRender
            visible={visible}
            title="Configuration"
            okText="Ok"
            cancelText={null}
            onCancel={() => {
                onCancel();
                form.resetFields();
                setMakerText(configValues['makerText']);
                setModelText(configValues['modelText']);
                setBuilderText(configValues['builderText']);
                setVersionText(configValues['versionText']);
                setIsDirty(false);
            }}
            closeIcon={<CloseOutlined />}
            okButtonProps={isDirty ? {} : { disabled: true }}
            onOk={() => {
                form.validateFields()
                    .then((values) => {
                        setIsPageLoading(true);
                        dataService.firestore
                            .collection('mappings')
                            .doc(fileId)
                            .set({
                                maker: values['maker'],
                                makerText: makerText,
                                model: values['model'],
                                modelText: modelText,
                                builder: values['builder'],
                                builderText: builderText,
                                version: values['version'],
                                versionText: versionText,
                                fileId: fileId
                            })
                            .then((result) => {
                                form.resetFields();
                                onCreate(values);
                                setIsDirty(false);
                                setIsPageLoading(false);
                            });
                    })
                    .catch((info) => {
                        message.warn('Validation Failed.');
                        console.log('Validation Failed:', info);
                    });
            }}
        >
            <Form
                form={form}
                layout="horizontal"
                name="form_in_modal"
                initialValues={configValues}
                onValuesChange={onValuesChange}
            >
                <Form.Item
                    label="Maker"
                    name="maker"
                    rules={[{ required: true, message: 'Please input maker!' }]}
                >
                    <Select
                        {...selectProp}
                        onSearch={makerHandleSearch}
                        onChange={(value, option) => {
                            setMakerText(option.children);
                            form.setFieldsValue({
                                model: ''
                            });
                            setModelText(option.children);
                            setModelData([]);
                        }}
                    >
                        {makerOptions}
                    </Select>
                </Form.Item>

                <Spin spinning={modelLoading}>
                    <Form.Item
                        label="Model"
                        name="model"
                        rules={[
                            { required: true, message: 'Please input model!' }
                        ]}
                    >
                        <Select
                            {...selectProp}
                            onSearch={modelHandleSearch}
                            onChange={(value, option) => {
                                setModelText(option.children);
                            }}
                        >
                            {modelOptions}
                        </Select>
                    </Form.Item>
                </Spin>

                <Form.Item label="Builder" name="builder">
                    <Select
                        {...selectProp}
                        onSearch={builderHandleSearch}
                        onChange={(value, option) => {
                            setBuilderText(option.children);
                        }}
                    >
                        {builderOptions}
                    </Select>
                </Form.Item>

                <Spin spinning={versionLoading}>
                    <Form.Item
                        label="Version"
                        name="version"
                        rules={[
                            { required: true, message: 'Please input version!' }
                        ]}
                    >
                        <Select
                            {...selectProp}
                            onSearch={versionHandleSearch}
                            onChange={(value, option) => {
                                setVersionText(option.children);
                            }}
                        >
                            {versionOptions}
                        </Select>
                    </Form.Item>
                </Spin>
            </Form>
        </Modal>
    );
};

class ConfigModal extends Component {
    state = {
        visible: false,
        configValues: {},
        makerData: [],
        makerData: [],
        modelData: [],
        builderData: [],
        versionData: [],

        makerText: '',
        modelText: '',
        builderText: '',
        versionText: ''
    };

    onCreate = (values) => {
        console.log('Received values of form: ', values);
        this.setState({ visible: false });
        message.success('Mappings saved successfully.');
    };

    render() {
        return (
            <React.Fragment>
                <Button
                    // type="primary"
                    // icon={<SettingOutlined />}
                    onClick={() => {
                        this.props.setIsLoading(true);
                        dataService.firestore
                            .collection('mappings')
                            .doc(this.props.fileId)
                            .get()
                            .then((respose) => {
                                let configValues = respose.data() || {};
                                this.setState({
                                    makerData: configValues['maker']
                                        ? [
                                              {
                                                  value: configValues['maker'],
                                                  text:
                                                      configValues['makerText']
                                              }
                                          ]
                                        : [],
                                    modelData: configValues['model']
                                        ? [
                                              {
                                                  value: configValues['model'],
                                                  text:
                                                      configValues['modelText']
                                              }
                                          ]
                                        : [],
                                    builderData: configValues['builder']
                                        ? [
                                              {
                                                  value:
                                                      configValues['builder'],
                                                  text:
                                                      configValues[
                                                          'builderText'
                                                      ]
                                              }
                                          ]
                                        : [],
                                    versionData: configValues['version']
                                        ? [
                                              {
                                                  value:
                                                      configValues['version'],
                                                  text:
                                                      configValues[
                                                          'versionText'
                                                      ]
                                              }
                                          ]
                                        : [],
                                    configValues: configValues,
                                    visible: true
                                });
                                this.props.setIsLoading(false);
                            });
                    }}
                    size="middle"
                >
                    Mapping
                </Button>
                <CollectionCreateForm
                    visible={this.state.visible}
                    onCreate={this.onCreate}
                    onCancel={() => {
                        this.setState({
                            visible: false,
                            makerData: [],
                            modelData: [],
                            builderData: [],
                            versionData: [],
                            configValues: {}
                        });
                    }}
                    configValues={this.state.configValues}
                    filePath={this.props.filePath}
                    fileId={this.props.fileId}
                    makerData={this.state.makerData}
                    setMakerData={(data) => this.setState({ makerData: data })}
                    modelData={this.state.modelData}
                    setModelData={(data) => this.setState({ modelData: data })}
                    builderData={this.state.builderData}
                    setBuilderData={(data) =>
                        this.setState({ builderData: data })
                    }
                    versionData={this.state.versionData}
                    setVersionData={(data) =>
                        this.setState({ versionData: data })
                    }
                    makerText={this.state.makerText}
                    setMakerText={(val) => this.setState({ makerText: val })}
                    modelText={this.state.modelText}
                    setModelText={(val) => this.setState({ modelText: val })}
                    builderText={this.state.builderText}
                    setBuilderText={(val) =>
                        this.setState({ builderText: val })
                    }
                    versionText={this.state.versionText}
                    setVersionText={(val) =>
                        this.setState({ versionText: val })
                    }
                />
            </React.Fragment>
        );
    }
}

export default ConfigModal;



// WEBPACK FOOTER //
// ./demo/src/ConfigModal.js